import { IServiceOpcode, ISearchResult, IOpcodeCount, IResponse, ISyncStatus, Department, IOnlineCodeSortDetail } from '@signal/asp-data-commons';
import { OpCodeService } from './../../shared/services/opcode.service';
import { MileageService } from './../../shared/services/mileage.service';
import { AlertService } from '../../shared/services/alert.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import * as fromActions from './opcode.actions';
import { mergeMap, switchMap, map, catchError } from 'rxjs/operators';
import { toData } from '../app.adapter';
import * as fromServiceActions from './../service-appointment/service-appointment.actions';
import { ServiceAppointmentState } from '../service-appointment/service-appointment.reducer';

@Injectable()
export class OpCodeEffects {
  private availabilityType = 'service';
  somethingWentWrongPleaseReloadThePage = 'otherTexts.somethingWentWrongPleaseReloadThePage';

  
  loadAllOpCodes: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadAllOpCodes>(
      fromActions.OpCodeActionTypes.LOAD_ALL_OPCODES
    ),
    mergeMap((action: fromActions.LoadAllOpCodes) =>
      this.opCodeService.getAllOpCodes(action.payload.order, action.payload.sort, '0', action.payload.limit, action.payload.query).pipe(
        map((opCodeDetails: ISearchResult<IServiceOpcode[]>) => new fromActions.LoadAllOpCodesSuccess({ opCodeDetails })),
        catchError(err => {
          this.alertService.showAlert(this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.LoadAllOpCodesFailure({ error: err }))
        })
      )
    )
  ));

  
  loadOnlineOpCodes: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadOnlineOpCodes>(
      fromActions.OpCodeActionTypes.LOAD_ONLINE_OPCODES
    ),
    mergeMap((action: fromActions.LoadOnlineOpCodes) =>
      this.opCodeService.getOnlineOpCodes(action.payload.order, action.payload.sort, '0', action.payload.limit, action.payload.query).pipe(
        map((opCodeDetails: ISearchResult<IServiceOpcode[]>) => new fromActions.LoadOnlineOpCodesSuccess({ opCodeDetails })),
        catchError(err => {
          this.alertService.showAlert(this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.LoadOnlineOpCodesFailure({ error: err }))
        })
      )
    )
  ));


  
  loadOpCodesCount: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadOpCodesCount>(
      fromActions.OpCodeActionTypes.LOAD_OPCODES_COUNT
    ),
    mergeMap((action: fromActions.LoadOpCodesCount) =>
      this.opCodeService.getOpCodesCount().pipe(
        map((opCodeCountDetails: IOpcodeCount) => new fromActions.LoadOpCodesCountSuccess({
          opCodeCountDetails
        })),
        catchError(err => {
          this.alertService.showAlert(this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.LoadOpCodesCountFailure({ error: err }))
        })
      )
    )
  ));

  
  loadOnlineSortingDetails: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadOnlineSortOrder>(
      fromActions.OpCodeActionTypes.LOAD_ONLINE_SORT_ORDER
    ),
    mergeMap((action: fromActions.LoadOnlineSortOrder) =>
      this.opCodeService.getOpCodeSortingDetails().pipe(
        map((onlineSortOrder: IOnlineCodeSortDetail) => new fromActions.LoadOnlineSortOrderSuccess({
          onlineSortOrder
        })),
        catchError(err => {
          this.alertService.showAlert(this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.LoadOnlineSortOrderFailure({ error: err }))
        })
      )
    )
  ));

  
  updateOnlineSortingDetails: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.UpdateOnlineSortOrder>(
      fromActions.OpCodeActionTypes.UPDATE_ONLINE_SORT_ORDER
    ),
    mergeMap((action: fromActions.UpdateOnlineSortOrder) =>
      this.opCodeService.updateOpCodeSortingDetails(action.payload.onlineSortOrder).pipe(
        map((onlineSortOrder: IOnlineCodeSortDetail) => new fromActions.UpdateOnlineSortOrderSuccess({
          onlineSortOrder
        })),
        catchError(err => {
          this.alertService.showAlert(this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.UpdateOnlineSortOrderFailure({ error: err }))
        })
      )
    )
  ));
  

  
  createOpCode: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.CreateOpCode>(
      fromActions.OpCodeActionTypes.CREATE_OPCODE
    ),
    mergeMap((action: fromActions.CreateOpCode) => {
      const opCode = action.payload.opcode;
      return this.opCodeService.createOpcode(opCode).pipe(
        switchMap((createdOpCode: IResponse<IServiceOpcode>) => toData(createdOpCode, 201)),
        map(
          (createdOpCode: IServiceOpcode) => {
            this.alertService.showAlert('otherTexts.createdSuccessfully', `${opCode.opcode}`, '', 5000, false);
            return new fromActions.CreateOpCodeSuccess({ opcode: createdOpCode })
          },
        ),
        catchError(err => {
          this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.CreateOpCodeFailure({ error: err }))
        })
      )
    }
    )
  ));

  
  updateOpCode: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.UpdateOpCode>(
      fromActions.OpCodeActionTypes.UPDATE_OPCODE
    ),
    mergeMap((action: fromActions.UpdateOpCode) => {
      const opCode = action.payload.opcode;
      return this.opCodeService.updateOpcode(opCode.opcode, opCode).pipe(
        switchMap((updatedOpCode: IResponse<IServiceOpcode>) => toData(updatedOpCode)),
        map(
          (updatedOpCode: IServiceOpcode) => {
            if(action.payload.isLoadNotificationData){
              this.serviceAppointmentStore.dispatch(new fromServiceActions.LoadNotificationDashboardData({department: Department.SERVICE}));
            }
            this.alertService.showAlert('otherTexts.detailsUpdatedSuccessfully', `${updatedOpCode.opcode}`, '', 5000, false);
            return new fromActions.UpdateOpCodeSuccess({ opcode: updatedOpCode })
          },
        ),
        catchError(err => {
          this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.UpdateOpCodeFailure({ error: err }))
        })
      )
    }
    )
  ));

  
  fetchOpcodeById: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.FetchOpcodeById>(
      fromActions.OpCodeActionTypes.FETCH_OPCODE_BY_ID
    ),
    mergeMap((action: fromActions.FetchOpcodeById) => {
      const opCode = action.payload.opcode;
      return this.opCodeService.getOpcodeById(opCode).pipe(
        switchMap((opCodeDetails: IResponse<IServiceOpcode>) => toData(opCodeDetails)),
        map(
          (opCodeDetails: IServiceOpcode) => {
            this.alertService.showAlert('otherTexts.detailsFetchedSuccessfully',`${opCodeDetails.opcode}`, '', 5000, false);
            return new fromActions.FetchOpcodeByIdSuccess({ service: opCodeDetails })
          },
        ),
        catchError(err => {
          this.alertService.showAlert('otherTexts.notAValidOpcode');
          return of(new fromActions.FetchOpcodeByIdFailure({ error: err }))
        })
      )
    }
    )
  ));

  
  loadMileages: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadMileages>(
      fromActions.OpCodeActionTypes.LOAD_MILEAGES
    ),
    mergeMap((action: fromActions.LoadMileages) =>
      this.mileageService.getMileages().pipe(
        switchMap((milages: IResponse<number[]>) => toData(milages)),
        map((mileages: number[]) => new fromActions.LoadMileagesSuccess({ mileages })),
        catchError(err => {
          this.alertService.showAlert(this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.LoadMileagesFailure({ error: err }))
        })
      )
    )
  ));

  
  loadSyncStatus: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadSyncStatus>(
      fromActions.OpCodeActionTypes.LOAD_SYNC_STATUS
    ),
    mergeMap((action: fromActions.LoadSyncStatus) =>
      this.opCodeService.getOpcodeSyncStatus().pipe(
        switchMap((status: IResponse<ISyncStatus>) => toData(status)),
        map((status: ISyncStatus) => new fromActions.LoadSyncStatusSuccess({ status })),
        catchError(err => {
          this.alertService.showAlert(this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.LoadSyncStatusFailure({ error: err }))
        })
      )
    )
  ));

  
  updateFromDms: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.UpdateFromDms>(
      fromActions.OpCodeActionTypes.UPDATE_FROM_DMS
    ),
    mergeMap((action: fromActions.UpdateFromDms) => {
      return this.opCodeService.updateOpcodesFromDms().pipe(
        switchMap((status: IResponse<ISyncStatus>) => toData(status, 201)),
        map(
          (status: ISyncStatus) => {
            this.alertService.showAlert(`otherTexts.dmsSyncInitiatedSuccessfully`, '', '', 5000, false);
            return new fromActions.UpdateFromDmsSuccess({ status })
          },
        ),
        catchError(err => {
          this.alertService.showAlert('otherTexts.somethingWentWrongPleaseTryAgain');
          return of(new fromActions.UpdateFromDmsFailure({ error: err }))
        })
      )
    }
    )
  ));

  
  updateAspOpCodeName: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.UpdateAspOpCodeName>(
      fromActions.OpCodeActionTypes.UPDATE_ASP_OPCODE_NAME
    ),
    mergeMap((action: fromActions.UpdateAspOpCodeName) => {
      const payload = action.payload;
      return this.opCodeService.updateAspOpcode(payload.aspServiceCode, payload.opcode).pipe(
        switchMap((updatedAspOpCode: IResponse<IServiceOpcode>) => toData(updatedAspOpCode)),
        map(
          (updatedAspOpCode: IServiceOpcode) => {
            this.alertService.showAlert('otherTexts.detailsUpdatedSuccessfully', `${payload.aspServiceCode}`, '', 5000, false);
            return new fromActions.UpdateAspOpCodeNameSuccess({ aspServiceCode: payload.aspServiceCode ,opcode: updatedAspOpCode })
          },
        ),
        catchError(err => {
          this.alertService.showAlert(err?.error?.status?.messages[0] || this.somethingWentWrongPleaseReloadThePage);
          return of(new fromActions.UpdateAspOpCodeNameFailure({ error: err }))
        })
      )
    }
    )
  ));

  
  deleteAspServiceCode: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.DeleteAspServiceCode>(
      fromActions.OpCodeActionTypes.DELETE_ASP_SERVICE_CODE
    ),
    mergeMap((action: fromActions.DeleteAspServiceCode) =>
      this.opCodeService.deleteAspServiceCode(action.payload.aspServiceCode).pipe(
        switchMap((deletedAspOpCode: IResponse<IServiceOpcode>) => toData(deletedAspOpCode)),
        map((deletedAspOpCode: IServiceOpcode) => {
          this.alertService.showAlert('otherTexts.deletedSuccessfully', `${deletedAspOpCode.opcode}`, '', 5000, false);
          return new fromActions.DeleteAspServiceCodeSuccess({opcode: deletedAspOpCode})
        }),
        catchError(err => {
          this.alertService.showAlert(err?.error?.status?.messages[0] || 'otherTexts.somethingWentWrongPleaseTryAgain');
          return of(new fromActions.DeleteAspServiceCodeFail(err))
        })
      )
    )
  ));

  constructor(
    private actions$: Actions,
    private opCodeService: OpCodeService,
    // private store$: Store<OpCodeState>,
    private mileageService: MileageService,
    private alertService: AlertService,
    private serviceAppointmentStore: Store<ServiceAppointmentState>
  ) {
  }
}
